import { ApplicationInsights, DistributedTracingModes, SeverityLevel } from "@microsoft/applicationinsights-web"
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createContext, useContext } from "react";

interface Logger {
    log(...data: any[]): void
    info(...data: any[]): void
    error(...data: any[]): void
    warn(...data: any[]): void
    debug(...data: any[]): void
    pageView(name: string, url: string): void
}

interface CreateLoggerProps {
    interceptors: Logger[]
    captureConsole: boolean
    logLevel?: LogLevel
}

export enum LogLevel {
    DEBUG = 1,
    INFO = 2,
    LOG = 3,
    WARN = 4,
    ERROR = 5
}

export const createLogger: (props: CreateLoggerProps) => Logger = ({interceptors, captureConsole, logLevel = LogLevel.DEBUG}) => {

    const _log = console.log
    const _info = console.info
    const _warn = console.warn
    const _error = console.error

    const debug = (...data: any[]) => {
        if (logLevel > LogLevel.DEBUG) return
        interceptors.forEach(interceptor => interceptor.debug(...data))
        captureConsole && _log(...data)
    }

    const log = (...data: any[]) => {
        if (logLevel > LogLevel.LOG) return
        interceptors.forEach(interceptor => interceptor.log(...data))
        captureConsole && _log(...data)    
    }

    const info = (...data: any[]) => {
        if (logLevel > LogLevel.INFO) return
        interceptors.forEach(interceptor => interceptor.info(...data))
        captureConsole && _info(...data)
    }

    const warn = (...data: any[]) => {
        if (logLevel > LogLevel.WARN) return
        interceptors.forEach(interceptor => interceptor.warn(...data))
        captureConsole && _warn(...data)
    }

    const error = (...data: any[]) => {
        interceptors.forEach(interceptor => interceptor.error(...data))
        captureConsole && _error(...data)
    }

    const pageView = (name: string, url: string) => {
        interceptors.forEach(interceptor => interceptor.pageView(name, url))
    }

    if (captureConsole) {
        console.log = log
        console.warn = log
        console.error = log
        console.debug = log
        console.info = info
    }

    return {
        debug,
        log,
        info,
        warn,
        error,
        pageView
    }
}


interface AppInsightsLoggerProps {
    instrumentationKey: string
}

export const AppInsightsLogger: (props: AppInsightsLoggerProps) => Logger = ({instrumentationKey}): Logger => {

    const reactPlugin = new ReactPlugin();
    const appInsights = new ApplicationInsights({
        config: {
            instrumentationKey,
            enableAutoRouteTracking: false,
            extensions: [reactPlugin],
            distributedTracingMode: DistributedTracingModes.AI_AND_W3C
        }
    })
    appInsights.loadAppInsights();
    
   return {
       log: (...data) => {
           appInsights.trackTrace({message: data.join(" "), severityLevel: SeverityLevel.Information})
       },
       error: (...data) => {
        const error = data.find(d => d instanceof Error) ?? Error(data.join(" "))
           appInsights.trackException({exception: error})
           appInsights.flush()
       },
       info: (...data) => {
        appInsights.trackTrace({message: data.join(" "), severityLevel: SeverityLevel.Information})
       },
       warn: (...data) => {
        appInsights.trackTrace({message: data.join(" "), severityLevel: SeverityLevel.Warning})
       },
       debug: (...data) => {
        appInsights.trackTrace({message: data.join(" "), severityLevel: SeverityLevel.Verbose})
       },
       pageView: (name, url) => {
            appInsights.trackPageView({name, uri: url})
       }
   }
}

interface LoggerContextProps {
    logger: Logger
}

export const LoggerContext = createContext({} as LoggerContextProps)

export const LoggerProvider: React.FC<LoggerContextProps> = ({children, logger}) => {
    return (
        <LoggerContext.Provider value={{logger}}>
            {children}
        </LoggerContext.Provider>
    )
}

export const useLogger = () => {
    const {logger} = useContext(LoggerContext)
    return logger
}